/*
 * ==========================================================================
 * SingRank - GEO / AI-visibility pixel for ablink.sg            v1.0.1
 * --------------------------------------------------------------------------
 * WHY THIS EXISTS
 * Ablink was the only active client with NO AI-visibility tracking at all.
 * Measured 2026-08-26 against ai_referral_log over 30 days:
 *
 *   pullupstand 107 | ifgshipping 28 | rcs 27 | renocontractorsg 11 | singrank 4
 *   ablink ......... DOES NOT APPEAR
 *
 * That was never an honest zero. Ablink simply had no px.gif tag, so every
 * visit arriving from ChatGPT, Gemini, Claude, Perplexity or Copilot was
 * invisible to us. Other clients show real volume from exactly those sources,
 * so there is no reason to think Ablink has none.
 *
 * WHAT IT DOES
 * Fires ONE 1x1 gif at leads.rumah.live, and ONLY when the visit is actually
 * AI-related. It is deliberately NOT a pageview beacon - a normal Google or
 * direct visit sends nothing at all.
 *
 *   k=referral  a human arrived from an AI assistant
 *   k=crawler   an AI bot fetched the page
 *
 * THREE WAYS A REFERRAL IS DETECTED, because one is not enough:
 *   1. document.referrer host (chatgpt.com, gemini.google.com, claude.ai, ...)
 *   2. utm_source on the URL - AI assistants increasingly tag their outbound
 *      links instead of sending a referrer. Real hits already in our data look
 *      like "?utm_source=chatgpt.com" and "?utm_source=copilot.com", and those
 *      carry NO referrer, so without this check they would be lost.
 *   3. a "ref" / "source" param carrying the same names.
 *
 * SLUG IS "ablink" - the canonical short form, matching rcs / pullupstand /
 * ifgshipping. NOTE the lead tracker in sections/ablink-wa-widget.liquid sends
 * the full domain "ablink.sg" instead, which is a known defect: it derives the
 * slug from location.hostname, which is why renaming the historical rows never
 * stuck. That is NOT fixed here on purpose - changing the site slug without
 * renaming the stored rows in the same pass would split ablink into two buckets,
 * exactly what already happened to rcs / renocontractorsg (27 and 11 hits, one
 * client, two buckets). Fix both together or neither.
 *
 * SAFETY
 * - Renders nothing, blocks nothing, and never runs on a non-AI visit.
 * - No cookies, no localStorage, no personal data. Only page URL, title and
 *   the referring source - the same three fields every other client sends.
 * - Guarded by window.__SR_GEO_PIXEL__ so a second copy is a no-op.
 * - Wrapped in try/catch throughout; on failure the page is unaffected.
 * - No regular expressions and no escape sequences, by house rule.
 *
 * DEBUG
 *   window.SR_GEO_PIXEL.state()  what it decided, and whether it sent
 *
 * Code by SingRank (https://singrank.com) for AB Link Pte Ltd. Created 2026-08-26.
 * ==========================================================================
 */
(function () {
  'use strict';

  if (window.__SR_GEO_PIXEL__) { return; }
  window.__SR_GEO_PIXEL__ = true;

  var ENDPOINT = 'https://leads.rumah.live/px.gif';
  var SLUG = 'ablink';

  // Host fragments, matched against the referrer host and against utm_source.
  // Kept as fragments rather than exact hosts so gemini.google.com,
  // chat.openai.com and chatgpt.com all resolve without listing every variant.
  var SOURCES = [
    ['chatgpt',      'chatgpt'],
    ['openai',       'chatgpt'],
    ['oai-search',   'chatgpt'],
    ['gemini',       'gemini'],
    ['bard.google',  'gemini'],
    ['claude.ai',    'claude'],
    ['anthropic',    'claude'],
    ['perplexity',   'perplexity'],
    ['copilot',      'copilot'],
    ['bing.com/chat','copilot'],
    ['you.com',      'you'],
    ['poe.com',      'poe'],
    ['phind',        'phind'],
    ['mistral',      'mistral'],
    ['grok',         'grok'],
    ['x.ai',         'grok'],
    ['deepseek',     'deepseek'],
    ['meta.ai',      'meta']
  ];

  // Crawler user agents. Most AI crawlers never execute JavaScript, so this
  // branch fires rarely - it is here for the rendering fetchers that do.
  var BOTS = [
    ['gptbot',            'chatgpt'],
    ['oai-searchbot',     'chatgpt'],
    ['chatgpt-user',      'chatgpt'],
    ['claudebot',         'claude'],
    ['claude-web',        'claude'],
    ['anthropic-ai',      'claude'],
    ['perplexitybot',     'perplexity'],
    ['perplexity-user',   'perplexity'],
    ['google-extended',   'gemini'],
    ['googleother',       'gemini'],
    ['applebot-extended', 'applebot'],
    ['bingbot',           'copilot'],
    ['ccbot',             'ccbot'],
    ['bytespider',        'bytespider'],
    ['amazonbot',         'amazonbot'],
    ['youbot',            'you'],
    ['meta-externalagent','meta'],
    ['facebookbot',       'meta'],
    ['diffbot',           'diffbot'],
    ['timpibot',          'timpi']
  ];

  var STATE = { checked: false, kind: '', source: '', via: '', sent: false };

  function low(s) { return String(s == null ? '' : s).toLowerCase(); }

  function firstMatch(haystack, table) {
    var h = low(haystack);
    if (!h) { return ''; }
    for (var i = 0; i < table.length; i++) {
      if (h.indexOf(table[i][0]) > -1) { return table[i][1]; }
    }
    return '';
  }

  function param(name) {
    try {
      var q = window.location.search || '';
      if (!q) { return ''; }
      var parts = q.substring(1).split('&');
      for (var i = 0; i < parts.length; i++) {
        var eq = parts[i].indexOf('=');
        if (eq < 0) { continue; }
        if (decodeURIComponent(parts[i].substring(0, eq)).toLowerCase() === name) {
          return decodeURIComponent(parts[i].substring(eq + 1).split('+').join(' '));
        }
      }
    } catch (e) {}
    return '';
  }

  function referrerHost() {
    try {
      var r = document.referrer || '';
      if (!r) { return ''; }
      // Same-site navigation is not a referral from anywhere.
      var host = new URL(r).hostname;
      if (low(host).indexOf(low(window.location.hostname)) > -1) { return ''; }
      return host;
    } catch (e) { return ''; }
  }

  function decide() {
    STATE.checked = true;

    // 1. a real AI bot fetching the page
    var bot = firstMatch(navigator.userAgent, BOTS);
    if (bot) {
      STATE.kind = 'crawler';
      STATE.source = bot;
      STATE.via = 'user-agent';
      return true;
    }

    // 2. a human sent here by an AI assistant, by referrer
    var byRef = firstMatch(referrerHost(), SOURCES);
    if (byRef) {
      STATE.kind = 'referral';
      STATE.source = byRef;
      STATE.via = 'referrer';
      return true;
    }

    // 3. a human sent here by an AI assistant that tagged the link instead of
    //    passing a referrer. This is how most ChatGPT and Copilot hits already
    //    reach our other clients, so it is not an edge case.
    var tagged = param('utm_source') || param('ref') || param('source');
    var byTag = firstMatch(tagged, SOURCES);
    if (byTag) {
      STATE.kind = 'referral';
      STATE.source = byTag;
      STATE.via = 'utm';
      return true;
    }

    return false;
  }

  function send() {
    try {
      var q = ENDPOINT
        + '?c=' + encodeURIComponent(SLUG)
        + '&k=' + encodeURIComponent(STATE.kind || 'referral')
        + '&s=' + encodeURIComponent(STATE.source || 'unknown')
        + '&u=' + encodeURIComponent(window.location.href)
        + '&t=' + encodeURIComponent((document.title || '').substring(0, 300))
        + '&ref=' + encodeURIComponent(document.referrer || '')
        + '&_=' + String(new Date().getTime());
      var img = new Image(1, 1);
      img.referrerPolicy = 'no-referrer-when-downgrade';
      img.src = q;
      STATE.sent = true;
      return q;
    } catch (e) { return ''; }
  }

  window.SR_GEO_PIXEL = {
    version: '1.0.0',
    slug: SLUG,
    state: function () { return STATE; }
  };

  try {
    if (decide()) { send(); }
  } catch (e) {}
})();
